/** Operator Desk company vs personal scope — empty-state clarity. */

export type OperatorDeskMembershipTier = 'owner' | 'admin' | 'office' | 'field' | 'none';

export type OperatorDeskScope = {
  mode: 'company' | 'personal';
  company: { id: string; name: string; status: string } | null;
  membershipTier: OperatorDeskMembershipTier;
  hasJobMasters: boolean;
  companyVerified: boolean;
  verifiedEmailCount: number;
  roleLabel: string;
  canManageFunding?: boolean;
  canAssignWork?: boolean;
  financeOnly?: boolean;
};

export type OperatorDeskEmptySurface =
  | 'attention'
  | 'projects'
  | 'approvals-pending'
  | 'approvals-records'
  | 'history'
  | 'gallery'
  | 'arrivals'
  | 'comms'
  | 'payouts'
  | 'payouts-funding';

export type OperatorDeskEmptyCopy = {
  title: string;
  detail: string;
  scopeLine: string | null;
  action: 'assign' | 'company-dashboard' | 'sign-in' | null;
  actionLabel: string | null;
};

function companyName(scope: OperatorDeskScope | null | undefined): string | null {
  const name = scope?.company?.name?.trim();
  return name || null;
}

export function operatorDeskScopeLine(scope: OperatorDeskScope | null | undefined): string | null {
  if (!scope) return null;
  const name = companyName(scope);
  if (scope.mode === 'company' && name) {
    return `Company scope · ${name} · ${scope.roleLabel}`;
  }
  if (name) {
    return `Personal scope · ${name} on file · ${scope.roleLabel}`;
  }
  return `Personal scope · ${scope.roleLabel}`;
}

export function operatorDeskEmptyCopy(
  surface: OperatorDeskEmptySurface,
  scope: OperatorDeskScope | null | undefined,
  opts?: { filtered?: boolean; authRequired?: boolean; kindLabel?: string },
): OperatorDeskEmptyCopy {
  if (opts?.authRequired) {
    return {
      title: 'Sign in to load this queue',
      detail: 'Company and personal desk work stay private until you sign in.',
      scopeLine: null,
      action: 'sign-in',
      actionLabel: 'Sign in',
    };
  }
  if (opts?.filtered) {
    return {
      title:
        surface === 'projects'
          ? 'No matching projects.'
          : surface.startsWith('approvals')
            ? 'No matching jobs'
            : 'No matching work.',
      detail: 'Try clearing search or filters.',
      scopeLine: operatorDeskScopeLine(scope),
      action: null,
      actionLabel: null,
    };
  }

  const name = companyName(scope);
  const scopeLine = operatorDeskScopeLine(scope);

  if (scope?.mode === 'company') {
    const who = name || 'your company';
    if (surface === 'attention') {
      return {
        title: 'No attention items for company work',
        detail: `Nothing needs follow-up in ${who}'s loaded jobs right now.`,
        scopeLine,
        action: 'assign',
        actionLabel: 'Assign work',
      };
    }
    if (surface === 'projects') {
      return {
        title: 'No active company projects',
        detail: `Assign work under ${who} to start a project.`,
        scopeLine,
        action: 'assign',
        actionLabel: 'Assign work',
      };
    }
    if (surface === 'approvals-pending') {
      return {
        title: 'No jobs awaiting your approval',
        detail: `No completion reviews are waiting for ${who} in this scope.`,
        scopeLine,
        action: null,
        actionLabel: null,
      };
    }
    if (surface === 'approvals-records') {
      return {
        title: 'No completion records yet',
        detail: `Approved or finished jobs for ${who} will show up here.`,
        scopeLine,
        action: null,
        actionLabel: null,
      };
    }
    if (surface === 'history') {
      const label = opts?.kindLabel || 'items';
      return {
        title: `No ${label} in company scope`,
        detail: `Nothing is posted for ${who} in this Work Archive lane yet.`,
        scopeLine,
        action: 'assign',
        actionLabel: 'Assign work',
      };
    }
    if (surface === 'comms') {
      return {
        title: 'No one on location for voice dispatch',
        detail:
          `Pick a ${who} facility with fresh GPS presence, record ~45–60s, and send to company members on site. `
          + 'Assign work if you still need crews on the pad.',
        scopeLine,
        action: 'assign',
        actionLabel: 'Assign work',
      };
    }
    if (surface === 'payouts') {
      return {
        title: 'No funds held for company work',
        detail: `Nothing is in escrow for ${who} right now. Funding happens when a request is made — open Assign when you need a hold.`,
        scopeLine,
        action: 'assign',
        actionLabel: 'Open Assign',
      };
    }
    if (surface === 'payouts-funding') {
      return {
        title: 'No legacy unfunded jobs',
        detail: `Funding happens when a request is made. ${who} should not have open awaiting-funding rows — only rare drafts or legacy exceptions appear here.`,
        scopeLine,
        action: 'assign',
        actionLabel: 'Open Assign',
      };
    }
    if (surface === 'gallery') {
      return {
        title: 'No site photos in company scope yet',
        detail: `Photos attached to ${who} jobs will appear here by well / API.`,
        scopeLine,
        action: 'assign',
        actionLabel: 'Assign work',
      };
    }
    return {
      title: 'No contractor arrivals yet',
      detail: `Arrival history for ${who} jobs will show here after site visits.`,
      scopeLine,
      action: null,
      actionLabel: null,
    };
  }

  if (scope?.company && !scope.companyVerified) {
    const who = name || 'your company';
    return {
      title: 'Viewing personal work only',
      detail:
        `${who} is on file but not verified yet, so company job-master work stays hidden. `
        + 'Verify company email ownership to unlock company scope.',
      scopeLine,
      action: 'company-dashboard',
      actionLabel: 'Open Company Dashboard',
    };
  }
  if (
    scope?.membershipTier === 'field'
    || (scope?.membershipTier === 'office' && !scope.hasJobMasters)
  ) {
    const who = name || 'your company';
    return {
      title: 'Viewing personal work only',
      detail:
        `You're on ${who}, but this account does not have job-master scope. `
        + 'Ask a company admin to grant Job Masters if you should see company jobs.',
      scopeLine,
      action: 'company-dashboard',
      actionLabel: 'Open Company Dashboard',
    };
  }
  if (scope && scope.membershipTier === 'none') {
    const emailHint = scope.verifiedEmailCount
      ? 'Verified checkout emails are linked for personal guest jobs.'
      : 'Verify a work email if guest checkouts should appear on your personal desk.';
    return {
      title: 'Viewing personal work only',
      detail:
        'No company membership is linked to this account. '
        + `Join or register a verified company to see company job-master work. ${emailHint}`,
      scopeLine,
      action: 'company-dashboard',
      actionLabel: 'Open Company Dashboard',
    };
  }

  if (surface === 'attention') {
    return {
      title: 'No attention items in the loaded work.',
      detail: 'Assign work or refresh after jobs are posted.',
      scopeLine,
      action: 'assign',
      actionLabel: 'Assign work',
    };
  }
  if (surface === 'projects') {
    return {
      title: 'No active projects. Assign work to start a project.',
      detail: '',
      scopeLine,
      action: 'assign',
      actionLabel: 'Assign work',
    };
  }
  if (surface === 'approvals-pending') {
    return {
      title: 'No jobs awaiting your approval',
      detail: '',
      scopeLine,
      action: null,
      actionLabel: null,
    };
  }
  if (surface === 'approvals-records') {
    return {
      title: 'No completion records yet',
      detail: '',
      scopeLine,
      action: null,
      actionLabel: null,
    };
  }
  if (surface === 'history') {
    return {
      title: opts?.kindLabel ? `No ${opts.kindLabel}` : 'No items',
      detail: '',
      scopeLine,
      action: 'assign',
      actionLabel: 'Assign work',
    };
  }
  if (surface === 'comms') {
    return {
      title: 'Voice dispatch needs company scope',
      detail:
        'Job Masters send short audio to company members on a facility. '
        + 'Open Company Dashboard if you need Job Master access, or wait for a dispatch aimed at you.',
      scopeLine,
      action: 'company-dashboard',
      actionLabel: 'Open Company Dashboard',
    };
  }
  if (surface === 'payouts') {
    return {
      title: 'No funds held',
      detail: 'Fund at request time — open Assign when you need money held in escrow until release.',
      scopeLine,
      action: 'assign',
      actionLabel: 'Open Assign',
    };
  }
  if (surface === 'payouts-funding') {
    return {
      title: 'No legacy unfunded jobs',
      detail: 'Funding happens when a request is made. Only rare drafts or legacy unpaid exceptions appear here.',
      scopeLine,
      action: 'assign',
      actionLabel: 'Open Assign',
    };
  }
  if (surface === 'gallery') {
    return {
      title: 'No site photos yet',
      detail: 'Photos from desk jobs will group here by well / API.',
      scopeLine,
      action: 'assign',
      actionLabel: 'Assign work',
    };
  }
  return {
    title: 'No contractor arrivals yet.',
    detail: '',
    scopeLine,
    action: null,
    actionLabel: null,
  };
}


export type OperatorDeskDockMode = 'full' | 'comms';

/** In-app Desk entry: Job Masters and any company member (incl. field recipients). */
export function canOpenOperatorDesk(input: {
  canPostOperatorBoard?: boolean;
  canUseCompanyBudgets?: boolean;
  membershipTier?: OperatorDeskMembershipTier | null;
  /** Browser demo / Demo-arrival — show Operators like a real operator for testing. */
  demoSession?: boolean;
}): boolean {
  if (input.demoSession) return true;
  if (input.canPostOperatorBoard || input.canUseCompanyBudgets) return true;
  const tier = input.membershipTier || 'none';
  return tier === 'owner' || tier === 'admin' || tier === 'office' || tier === 'field';
}

/**
 * Full Job Master dock vs worker Comms-only dock.
 * Field members (and office without jobMasters scope) get Comms inbox/play only.
 * Sending stays AuthZ'd separately via hasJobMasters / canSend.
 */
export function operatorDeskDockMode(input: {
  canPostOperatorBoard?: boolean;
  canUseCompanyBudgets?: boolean;
  membershipTier?: OperatorDeskMembershipTier | null;
  hasJobMasters?: boolean;
  /** null = owner/admin full scopes; array = office allow-list */
  scopes?: string[] | null;
  /** Browser demo — full Job Master dock chrome for Operators pill testing. */
  demoSession?: boolean;
}): OperatorDeskDockMode {
  if (input.demoSession) return 'full';
  if (input.hasJobMasters || input.canUseCompanyBudgets) return 'full';
  if (input.canPostOperatorBoard) return 'full';
  const tier = input.membershipTier || 'none';
  if (tier === 'owner' || tier === 'admin') return 'full';
  if (tier === 'office') {
    const scopes = input.scopes;
    if (scopes == null || scopes.includes('jobMasters') || scopes.includes('finance')) return 'full';
    return 'comms';
  }
  return 'comms';
}

export function parseOperatorDeskScope(raw: unknown): OperatorDeskScope | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const mode = row.mode === 'company' || row.mode === 'personal' ? row.mode : null;
  if (!mode) return null;
  const tier = row.membershipTier;
  const membershipTier: OperatorDeskMembershipTier =
    tier === 'owner' || tier === 'admin' || tier === 'office' || tier === 'field' || tier === 'none'
      ? tier
      : 'none';
  const companyRaw = row.company && typeof row.company === 'object'
    ? row.company as Record<string, unknown>
    : null;
  const company = companyRaw && companyRaw.id
    ? {
        id: String(companyRaw.id),
        name: String(companyRaw.name || ''),
        status: String(companyRaw.status || ''),
      }
    : null;
  return {
    mode,
    company,
    membershipTier,
    hasJobMasters: Boolean(row.hasJobMasters),
    companyVerified: Boolean(row.companyVerified),
    verifiedEmailCount: Number(row.verifiedEmailCount) || 0,
    roleLabel: String(row.roleLabel || ''),
    ...(typeof row.canManageFunding === 'boolean' ? { canManageFunding: row.canManageFunding } : {}),
    ...(typeof row.canAssignWork === 'boolean' ? { canAssignWork: row.canAssignWork } : {}),
    ...(typeof row.financeOnly === 'boolean' ? { financeOnly: row.financeOnly } : {}),
  };
}
